import { useCallback, useRef, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { usePanelPosition } from '../hooks/usePanelPosition'
import { useDraggablePanel } from '../hooks/useDraggablePanel'
import { usePortalDismiss } from '../hooks/usePortalDismiss'
import type { OverlayStyleOverride } from '../types/persistence'
import { ALL_OVERLAYS } from '../utils/overlays'
import {
  POSITION_PLAN_NAME,
  MAX_PLAN_ENTRIES,
  appendPlanEntry,
  removePlanEntry,
  cascadeWeights,
  entryDisplayNumbers,
  entryPointIndices,
  parsePlanConfig,
  type PositionPlanConfig,
} from '../utils/positionPlanOverlay'
import {
  INPUT_BOX_WIDE_CLS,
  SECONDARY_BUTTON_CLS,
  ColorRow,
  DrawerHeader,
  DrawerNumberInput,
  DrawerSelect,
  SectionLabel,
  SettingRow,
  Toggle,
} from './drawerControls'
import { LineStyleControl } from './LineStyleControl'
import { primaryButtonCls } from './drawerControls-utils'
import {
  COORDINATE_EDITABLE,
  coordinateFields,
  normalizePoints,
  patchAnchorPoints,
  tsToLocalInput,
  localInputToTs,
} from './overlayCoordinates'
import { Z_MODAL } from './zLayers'
import { FIB_LEVELS, type FibExtendData, type FibLevelOverride } from '../utils/fibonacciOverlay'
import { DEFAULT_FILL_OPACITY, FILLABLE_SHAPES } from '../utils/shapeOverlays'
import { resolveAnnotationText } from '../utils/annotationOverlay'

// Re-exported so existing importers (index.ts) keep resolving it here.
export { COORDINATE_EDITABLE } from './overlayCoordinates'

/* ------------------------------------------------------------------ */
/*  Public API                                                          */
/* ------------------------------------------------------------------ */

/** Snapshot of the overlay handed in by EdgeChart on `overlay:edit`. */
export interface OverlayEditTarget {
  id: string
  name: string
  points?: Array<{ timestamp?: number; value?: number }>
  /** Style already normalized to our override shape by the host. */
  styles?: OverlayStyleOverride
  /** Opaque per-instance blob (e.g. fib per-level overrides). Shape owned by
   *  the overlay template, not this drawer. */
  extendData?: unknown
}

export interface OverlaySettingsDrawerProps {
  overlay: OverlayEditTarget
  /** Element the panel centers itself over (the chart area). The panel always
   *  centers — it never anchors beside the opening double-click. Draggable
   *  by its header once open, via `useDraggablePanel`. */
  centerIn?: { current: HTMLElement | null }
  /** Live-apply a style edit (color / width / line-style). Persists via the host. */
  onApplyStyle: (style: OverlayStyleOverride) => void
  /** Live-apply a coordinate edit (rebuilt points array). Persists via the host. */
  onApplyPoints: (points: Array<{ timestamp: number; value: number }>) => void
  /** Toggle drawing visibility (show/hide). */
  onSetVisible: (visible: boolean) => void
  /** Live-apply an extendData edit (e.g. fib per-level visibility). Persists
   *  via the host. Optional — only overlays with an extendData-backed tab
   *  (fibonacciLine's Levels section) call it. */
  onApplyExtendData?: (extendData: unknown) => void
  /** Persist the current style as this tool's per-tool GLOBAL default. Tools
   *  with extendData-backed settings (the Fib's per-level visibility) pass that
   *  too, so "Extensions off, save as default" really does shape the next Fib. */
  onSaveDefault: (name: string, style: OverlayStyleOverride, extendData?: unknown) => void
  /** Close the drawer. */
  onClose: () => void
}

/** Fallback line color when an overlay carries no explicit color override yet. */
const DEFAULT_LINE_COLOR = '#f97316'

const LABEL_BY_NAME: Record<string, string> = Object.fromEntries(
  ALL_OVERLAYS.map((o) => [o.name, o.label]),
)

function overlayLabel(name: string): string {
  return LABEL_BY_NAME[name] ?? name
}

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */

export function OverlaySettingsDrawer({
  overlay,
  centerIn,
  onApplyStyle,
  onApplyPoints,
  onSetVisible,
  onApplyExtendData,
  onSaveDefault,
  onClose,
}: OverlaySettingsDrawerProps) {
  // Pre-edit snapshots — cloned once on mount so Cancel reverts cleanly.
  //
  // Contract: every edit LIVE-APPLIES (and the host persists it immediately).
  // Cancel therefore re-applies these snapshots (which the host also persists),
  // so the net stored state equals the pre-edit state. There is no separate
  // "commit only on OK" — OK just closes because the edits already landed.
  const styleSnapshot = useRef<OverlayStyleOverride>({ ...(overlay.styles ?? {}) })
  const pointsSnapshot = useRef<Array<{ timestamp: number; value: number }>>(
    normalizePoints(overlay.points),
  )
  const visibleSnapshot = useRef<boolean>(true)

  const [workingStyle, setWorkingStyle] = useState<OverlayStyleOverride>(() => ({
    ...(overlay.styles ?? {}),
  }))
  const [workingPoints, setWorkingPoints] = useState<Array<{ timestamp: number; value: number }>>(
    () => normalizePoints(overlay.points),
  )
  const [visible, setVisible] = useState(true)

  // Fib per-level visibility. Sparse map — an absent key means "visible" (the
  // built-in default), only an explicit `visible: false` entry hides a level.
  const [workingLevels, setWorkingLevels] = useState<Record<string, FibLevelOverride>>(() => ({
    ...((overlay.extendData as FibExtendData | undefined)?.levels ?? {}),
  }))
  const levelsSnapshot = useRef<Record<string, FibLevelOverride>>({ ...workingLevels })

  const showCoordinates = COORDINATE_EDITABLE.has(overlay.name)
  const showFill = FILLABLE_SHAPES.has(overlay.name)
  const showFibLevels = overlay.name === 'fibonacciLine'
  const showPlan = overlay.name === POSITION_PLAN_NAME
  const showText = overlay.name === 'simpleAnnotation'

  // Annotation label: extendData as a plain string (the template's contract).
  // Snapshotted as a string too, so Cancel can restore a label that was blank.
  const [workingText, setWorkingText] = useState(() => resolveAnnotationText(overlay))
  const textSnapshot = useRef(workingText)

  // Position-plan config (risk type/value, cap, allocations) — extendData-backed
  // like the fib levels, parsed with defaults so a fresh drawing edits cleanly.
  const [workingPlan, setWorkingPlan] = useState<PositionPlanConfig>(() =>
    parsePlanConfig(overlay.extendData),
  )
  const planSnapshot = useRef<PositionPlanConfig>({
    ...workingPlan,
    weights: [...workingPlan.weights],
  })

  const drawerRef = useRef<HTMLDivElement>(null)

  // Centered over the chart — two-pass measured. Header-drag overrides this
  // once the user drags the panel (see `useDraggablePanel`).
  const centerPos = usePanelPosition(drawerRef, { centerIn })
  const { pos, onHeaderPointerDown } = useDraggablePanel(drawerRef, centerPos)

  // Cancel = restore all three snapshots, then close. Re-applies so the live
  // (already-persisted) edits are rewound to the pre-edit state.
  const handleCancel = useCallback(() => {
    // The host MERGES styles, so a fill turned on during this edit only goes
    // away if the restore names the fill keys; the snapshot may not have them.
    onApplyStyle(
      showFill
        ? { fillColor: undefined, fillColorOpacity: undefined, ...styleSnapshot.current }
        : styleSnapshot.current,
    )
    onApplyPoints(pointsSnapshot.current)
    onSetVisible(visibleSnapshot.current)
    // extendData restore is per-tool: only the tools with an extendData-backed
    // section have anything to rewind, and their payload shapes differ.
    if (showPlan) {
      onApplyExtendData?.(planSnapshot.current)
    } else if (showFibLevels) {
      onApplyExtendData?.({
        ...((overlay.extendData as object) ?? {}),
        levels: levelsSnapshot.current,
      })
    } else if (showText) {
      onApplyExtendData?.(textSnapshot.current)
    }
    onClose()
  }, [
    onApplyStyle,
    onApplyPoints,
    onSetVisible,
    onApplyExtendData,
    overlay.extendData,
    onClose,
    showFill,
    showPlan,
    showFibLevels,
    showText,
  ])

  // Esc + outside-click dismiss = cancel (revert snapshots + close). Deferred so
  // the opening double-click doesn't re-close it; the host color picker's Radix
  // portal is guarded inside the hook.
  usePortalDismiss(drawerRef, handleCancel)

  /* ---- Style edits ---- */

  const patchStyle = useCallback(
    (patch: Partial<OverlayStyleOverride>) => {
      setWorkingStyle((prev) => {
        const next = { ...prev, ...patch }
        onApplyStyle(next)
        return next
      })
    },
    [onApplyStyle],
  )

  /* ---- Coordinate edits ---- */

  // Rebuild the points array from an anchor patch and push it live. Preserves
  // every point past the anchor (e.g. a ray's direction point) untouched.
  const patchAnchor = useCallback(
    (patch: { timestamp?: number; value?: number }) => {
      setWorkingPoints((prev) => {
        const next = patchAnchorPoints(prev, patch)
        onApplyPoints(next)
        return next
      })
    },
    [onApplyPoints],
  )

  /* ---- Visibility ---- */

  const toggleVisible = useCallback(
    (next: boolean) => {
      setVisible(next)
      onSetVisible(next)
    },
    [onSetVisible],
  )

  /* ---- Fib per-level visibility ----
   *
   * Map stays sparse: a visible level has NO entry (or an entry with no
   * `visible` field), so a future palette/default change still reaches
   * levels the user never touched. Only an explicit `visible: false` is
   * ever written. */

  const setLevelVisible = useCallback(
    (key: string, vis: boolean) => {
      setWorkingLevels((prev) => {
        const next = { ...prev }
        if (vis) {
          const entry = { ...next[key] }
          delete entry.visible
          if (Object.keys(entry).length) next[key] = entry
          else delete next[key]
        } else {
          next[key] = { ...next[key], visible: false }
        }
        onApplyExtendData?.({ ...((overlay.extendData as object) ?? {}), levels: next })
        return next
      })
    },
    [onApplyExtendData, overlay.extendData],
  )

  // Group toggle (Retracements / Extensions) — same sparse-map rule, applied
  // to every member key in one state update and one live-apply call.
  const setGroupVisible = useCallback(
    (keys: string[], vis: boolean) => {
      setWorkingLevels((prev) => {
        const next = { ...prev }
        for (const key of keys) {
          if (vis) {
            const entry = { ...next[key] }
            delete entry.visible
            if (Object.keys(entry).length) next[key] = entry
            else delete next[key]
          } else {
            next[key] = { ...next[key], visible: false }
          }
        }
        onApplyExtendData?.({ ...((overlay.extendData as object) ?? {}), levels: next })
        return next
      })
    },
    [onApplyExtendData, overlay.extendData],
  )

  // "Saved" confirmation on the footer button, a second and a half, then back.
  const [savedFlash, setSavedFlash] = useState(false)
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (savedTimer.current) clearTimeout(savedTimer.current)
    },
    [],
  )
  const handleSaveDefault = useCallback(() => {
    // The Fib's per-level visibility lives in extendData, not style: without it
    // the saved default silently dropped "Extensions off" (user report 2026-09-08).
    const extendData = showFibLevels ? { levels: workingLevels } : undefined
    onSaveDefault(overlay.name, workingStyle, extendData)
    setSavedFlash(true)
    if (savedTimer.current) clearTimeout(savedTimer.current)
    savedTimer.current = setTimeout(() => setSavedFlash(false), 1500)
  }, [onSaveDefault, overlay.name, workingStyle, workingLevels, showFibLevels])

  const anchor = workingPoints[0] ?? { timestamp: 0, value: 0 }

  const panel = (
    <div
      ref={drawerRef}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      className="fixed flex w-[320px] flex-col overflow-hidden rounded-xl border border-primary/15 bg-popover text-[hsl(var(--chart-text))] shadow-2xl shadow-black/60"
      style={{
        zIndex: Z_MODAL,
        maxHeight: 'min(720px, 90vh)',
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      <DrawerHeader
        label={overlayLabel(overlay.name)}
        onClose={handleCancel}
        closeAriaLabel="Close drawing settings"
        onPointerDown={onHeaderPointerDown}
      />

      {/* Single scroll view — Style, Coordinates (when editable) and Visibility
          are stacked under lightweight section headers rather than split across
          tabs, so every control for the drawing is visible in one place. */}
      <div className="flex-1 overflow-y-auto px-3 py-3" style={{ scrollbarWidth: 'thin' }}>
        <div className="flex flex-col gap-2.5 py-1">
          <ColorRow
            label={showFill ? 'Border' : 'Color'}
            color={workingStyle.color ?? DEFAULT_LINE_COLOR}
            // The picker's opacity slider is the drawing's opacity, the same
            // `fillOpacity` field the Line popover's Transparency steps write.
            opacity={workingStyle.fillOpacity ?? 1}
            // Write fillOpacity only when the slider moved: a hue-only pick on
            // a shape with no explicit opacity must keep its translucent
            // default fill, not pin it to 1.
            onChange={(p) =>
              patchStyle({
                color: p.color,
                ...(p.opacity !== (workingStyle.fillOpacity ?? 1)
                  ? { fillOpacity: p.opacity }
                  : {}),
              })
            }
          />
          {/* One consolidated line editor — weight + style + transparency in a
              single popover. Overlays model only solid/dashed, so `dotted` is
              never offered nor emitted; opacity maps to `fillOpacity`. */}
          <SettingRow label="Line">
            <LineStyleControl
              size={workingStyle.size ?? 1}
              style={workingStyle.style ?? 'solid'}
              opacity={workingStyle.fillOpacity ?? 1}
              styleOptions={['solid', 'dashed']}
              onChange={(patch) =>
                patchStyle({
                  ...(patch.size !== undefined ? { size: patch.size } : {}),
                  ...(patch.style !== undefined
                    ? { style: patch.style === 'dotted' ? 'dashed' : patch.style }
                    : {}),
                  ...(patch.opacity !== undefined ? { fillOpacity: patch.opacity } : {}),
                })
              }
            />
          </SettingRow>
          {/* Closed shapes can shade the region they enclose. Off = outline
              only (the default); turning it on seeds a translucent fill in the
              border's color. Off writes the keys as undefined, not absent:
              the host merges styles, so an absent key would keep the fill. */}
          {showFill && (
            <>
              <SettingRow label="Background">
                <Toggle
                  checked={workingStyle.fillColor !== undefined}
                  onChange={(on) =>
                    patchStyle(
                      on
                        ? {
                            fillColor: workingStyle.color ?? DEFAULT_LINE_COLOR,
                            fillColorOpacity: DEFAULT_FILL_OPACITY,
                          }
                        : { fillColor: undefined, fillColorOpacity: undefined },
                    )
                  }
                  size="sm"
                  ariaLabel="Background"
                />
              </SettingRow>
              {workingStyle.fillColor !== undefined && (
                <ColorRow
                  label="Background color"
                  color={workingStyle.fillColor}
                  // The fill's own opacity; legacy and AI-drawn shapes have
                  // none yet, so show the alpha they actually render with.
                  opacity={
                    workingStyle.fillColorOpacity ??
                    workingStyle.fillOpacity ??
                    DEFAULT_FILL_OPACITY
                  }
                  onChange={(p) => patchStyle({ fillColor: p.color, fillColorOpacity: p.opacity })}
                />
              )}
            </>
          )}
        </div>

        {/* The annotation's label. Live-applies like every other edit; it is
            per drawing, so Save as default never carries it. */}
        {showText && (
          <>
            <SectionLabel>Text</SectionLabel>
            <div className="flex flex-col gap-3 py-1">
              <SettingRow label="Label">
                <input
                  type="text"
                  aria-label="Text"
                  placeholder="Add text"
                  value={workingText}
                  onChange={(e) => {
                    setWorkingText(e.target.value)
                    onApplyExtendData?.(e.target.value)
                  }}
                  className={INPUT_BOX_WIDE_CLS}
                />
              </SettingRow>
            </div>
          </>
        )}

        {showCoordinates && (
          <>
            <SectionLabel>Coordinates</SectionLabel>
            <CoordinatesTab
              name={overlay.name}
              timestamp={anchor.timestamp}
              value={anchor.value}
              onValueChange={(v) => patchAnchor({ value: v })}
              onTimeChange={(ts) => patchAnchor({ timestamp: ts })}
            />
          </>
        )}

        {showFibLevels && (
          <>
            <SectionLabel>Levels</SectionLabel>
            <FibLevelsTab
              workingLevels={workingLevels}
              onSetLevelVisible={setLevelVisible}
              onSetGroupVisible={setGroupVisible}
            />
          </>
        )}

        {showPlan && (
          <>
            <SectionLabel>Position Plan</SectionLabel>
            <PlanTab
              config={workingPlan}
              points={workingPoints}
              onPatchConfig={(patch) => {
                setWorkingPlan((prev) => {
                  const next = { ...prev, ...patch }
                  onApplyExtendData?.(next)
                  return next
                })
              }}
              onAddEntry={() => {
                // Same seed + weight-reset semantics as the on-chart pill.
                setWorkingPoints((prevPts) => {
                  const next = appendPlanEntry(prevPts, workingPlan)
                  if (!next) return prevPts
                  onApplyPoints(next.points)
                  setWorkingPlan(next.config)
                  onApplyExtendData?.(next.config)
                  return next.points
                })
              }}
              onRemoveEntry={(entryIdx) => {
                // Same renormalize semantics as the % menu's "Remove entry".
                setWorkingPoints((prevPts) => {
                  const next = removePlanEntry(prevPts, workingPlan, entryIdx)
                  if (!next) return prevPts
                  onApplyPoints(next.points)
                  setWorkingPlan(next.config)
                  onApplyExtendData?.(next.config)
                  return next.points
                })
              }}
            />
          </>
        )}

        <SectionLabel>Visibility</SectionLabel>
        <VisibilityTab visible={visible} onToggle={toggleVisible} />
      </div>

      <Footer
        onCancel={handleCancel}
        onOk={onClose}
        onSaveDefault={handleSaveDefault}
        saved={savedFlash}
      />
    </div>
  )

  return createPortal(panel, document.body)
}

/* ------------------------------------------------------------------ */
/*  Header                                                              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  Coordinates tab                                                     */
/* ------------------------------------------------------------------ */

function CoordinatesTab({
  name,
  timestamp,
  value,
  onValueChange,
  onTimeChange,
}: {
  name: string
  timestamp: number
  value: number
  onValueChange: (v: number) => void
  onTimeChange: (ts: number) => void
}) {
  const { price: showPrice, time: showTime } = coordinateFields(name)
  return (
    <div className="flex flex-col gap-3 py-1">
      {showPrice && (
        <SettingRow label="#1 (price)">
          <DrawerNumberInput
            ariaLabel="Price"
            value={value}
            onChange={onValueChange}
            // Full precision — instruments vary (equities 2dp, FX 5dp, crypto
            // more), so we never round the price the user is editing.
            step="any"
            className="h-7 w-32 rounded border border-border-strong bg-field px-2 text-xs text-[hsl(var(--chart-text))] outline-hidden"
          />
        </SettingRow>
      )}
      {showTime && <TimeRow label="#1 (time)" timestamp={timestamp} onChange={onTimeChange} />}
    </div>
  )
}

function TimeRow({
  label,
  timestamp,
  onChange,
}: {
  label: string
  timestamp: number
  onChange: (ts: number) => void
}) {
  return (
    <SettingRow label={label}>
      <input
        type="datetime-local"
        aria-label="Time"
        value={tsToLocalInput(timestamp)}
        onChange={(e) => {
          const ts = localInputToTs(e.target.value)
          if (ts != null) onChange(ts)
        }}
        className="h-7 w-44 rounded border border-border-strong bg-field px-2 text-xs text-[hsl(var(--chart-text))] outline-hidden"
      />
    </SettingRow>
  )
}

/* ------------------------------------------------------------------ */
/*  Fib levels tab                                                     */
/* ------------------------------------------------------------------ */

/** Retracement levels (0–100%) grouped separately from the beyond-100%
 *  extension levels — matches how traders talk about the two. */
/* ------------------------------------------------------------------ */
/*  Position-plan section                                               */
/* ------------------------------------------------------------------ */

const PLAN_PCT_OPTIONS = [10, 20, 25, 33, 50, 66, 75, 100].map((v) => ({
  value: String(v),
  label: `${v}%`,
}))

function PlanTab({
  config,
  points,
  onPatchConfig,
  onAddEntry,
  onRemoveEntry,
}: {
  config: PositionPlanConfig
  points: Array<{ timestamp: number; value: number }>
  onPatchConfig: (patch: Partial<PositionPlanConfig>) => void
  onAddEntry: () => void
  onRemoveEntry: (entryIndex: number) => void
}) {
  // Index 1 is the stop; everything else is an entry (positionPlanOverlay.ts).
  const entryCount = Math.max(1, points.length - 1)
  const equal = 100 / entryCount
  return (
    <div className="flex flex-col gap-2">
      <SettingRow label="Risk type">
        <DrawerSelect
          ariaLabel="Risk type"
          value={config.riskType}
          options={[
            { value: 'dollars', label: 'Dollars' },
            { value: 'shares', label: 'Shares' },
          ]}
          onChange={(v) => onPatchConfig({ riskType: v === 'shares' ? 'shares' : 'dollars' })}
        />
      </SettingRow>
      <SettingRow label={config.riskType === 'shares' ? 'Shares' : 'Risk $'}>
        <DrawerNumberInput
          value={config.riskValue}
          min={1}
          fallback={100}
          onChange={(v) => onPatchConfig({ riskValue: Number(v) })}
        />
      </SettingRow>
      <SettingRow label="Max position $">
        <DrawerNumberInput
          value={config.maxPositionDollars}
          min={0}
          fallback={0}
          onChange={(v) => onPatchConfig({ maxPositionDollars: Number(v) })}
        />
      </SettingRow>
      <SettingRow label="Axis price labels">
        <Toggle
          checked={config.showAxisLabels !== false}
          onChange={(next) => onPatchConfig({ showAxisLabels: next })}
          size="sm"
          ariaLabel="Axis price labels"
        />
      </SettingRow>
      {entryCount > 1 &&
        (() => {
          // Rows follow the DISPLAY numbering (E1 = farthest from the stop),
          // and the cascade caps each row's options at what the earlier rows
          // left over. Every row is editable — including the last, so the
          // total may deliberately land under 100 (partial allocation; the
          // stop label then shows the scaled risk + alloc %).
          const indices = entryPointIndices(points.length)
          const numbers = entryDisplayNumbers(
            indices.map((i) => points[i]?.value ?? NaN),
            points[1]?.value ?? NaN,
          )
          const effective = Array.from({ length: entryCount }, (_, j) =>
            Math.round(config.weights[j] ?? equal),
          )
          return numbers
            .map((num, phys) => ({ num, phys }))
            .sort((a, b) => a.num - b.num)
            .map(({ num, phys }) => {
              const locked = effective.reduce((a, w, j) => (numbers[j] < num ? a + w : a), 0)
              const budget = 100 - locked
              return (
                <SettingRow key={phys} label={`E${num} position`}>
                  <span className="flex items-center gap-1">
                    <DrawerSelect
                      ariaLabel={`E${num} position`}
                      value={String(effective[phys])}
                      options={PLAN_PCT_OPTIONS.filter((o) => Number(o.value) <= budget)}
                      onChange={(v) =>
                        onPatchConfig({
                          weights: cascadeWeights(effective, numbers, phys, Number(v)),
                        })
                      }
                    />
                    <button
                      type="button"
                      aria-label={`Remove E${num}`}
                      title={`Remove E${num}`}
                      className="flex h-5 w-5 items-center justify-center rounded text-xs text-[hsl(var(--muted-foreground))] hover:bg-hover hover:text-destructive"
                      onClick={() => onRemoveEntry(phys)}
                    >
                      ✕
                    </button>
                  </span>
                </SettingRow>
              )
            })
        })()}
      <button
        type="button"
        className={SECONDARY_BUTTON_CLS}
        disabled={entryCount >= MAX_PLAN_ENTRIES}
        onClick={onAddEntry}
      >
        Add entry
      </button>
    </div>
  )
}

const FIB_RETRACEMENT_KEYS = ['0', '0.236', '0.382', '0.5', '0.618', '0.786', '1']
const FIB_EXTENSION_KEYS = ['1.618', '2.618', '3.618', '4.236']

function fibPercentLabel(key: string): string {
  const level = FIB_LEVELS.find((l) => l.key === key)
  return level ? `${(level.percent * 100).toFixed(1)}%` : key
}

function FibLevelsTab({
  workingLevels,
  onSetLevelVisible,
  onSetGroupVisible,
}: {
  workingLevels: Record<string, FibLevelOverride>
  onSetLevelVisible: (key: string, visible: boolean) => void
  onSetGroupVisible: (keys: string[], visible: boolean) => void
}) {
  return (
    <div className="flex flex-col gap-3 py-1">
      <FibLevelGroup
        label="Retracements"
        keys={FIB_RETRACEMENT_KEYS}
        workingLevels={workingLevels}
        onSetLevelVisible={onSetLevelVisible}
        onSetGroupVisible={onSetGroupVisible}
      />
      <FibLevelGroup
        label="Extensions"
        keys={FIB_EXTENSION_KEYS}
        workingLevels={workingLevels}
        onSetLevelVisible={onSetLevelVisible}
        onSetGroupVisible={onSetGroupVisible}
      />
    </div>
  )
}

function FibLevelGroup({
  label,
  keys,
  workingLevels,
  onSetLevelVisible,
  onSetGroupVisible,
}: {
  label: string
  keys: string[]
  workingLevels: Record<string, FibLevelOverride>
  onSetLevelVisible: (key: string, visible: boolean) => void
  onSetGroupVisible: (keys: string[], visible: boolean) => void
}) {
  // Group checked-state is DERIVED, never stored — it's true only when every
  // member level is currently visible, so a mixed group reads as "off".
  const groupVisible = keys.every((k) => workingLevels[k]?.visible !== false)
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] font-medium text-[hsl(var(--chart-text))]">{label}</span>
        <Toggle
          checked={groupVisible}
          onChange={(next) => onSetGroupVisible(keys, next)}
          size="sm"
          ariaLabel={groupVisible ? `Hide ${label}` : `Show ${label}`}
        />
      </div>
      <div className="flex flex-col gap-1 pl-1">
        {keys.map((key) => {
          const levelVisible = workingLevels[key]?.visible !== false
          const pct = fibPercentLabel(key)
          return (
            <div key={key} className="flex items-center justify-between gap-3 py-0.5">
              <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{pct}</span>
              <Toggle
                checked={levelVisible}
                onChange={(next) => onSetLevelVisible(key, next)}
                size="sm"
                ariaLabel={levelVisible ? `Hide ${pct} level` : `Show ${pct} level`}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Visibility tab                                                     */
/* ------------------------------------------------------------------ */

function VisibilityTab({
  visible,
  onToggle,
}: {
  visible: boolean
  onToggle: (visible: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="text-[11px] leading-snug text-[hsl(var(--muted-foreground))]">
        Show this drawing on the chart.
      </div>
      <Toggle
        checked={visible}
        onChange={onToggle}
        ariaLabel={visible ? 'Hide drawing' : 'Show drawing'}
      />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Footer                                                             */
/* ------------------------------------------------------------------ */

function Footer({
  onCancel,
  onOk,
  onSaveDefault,
  saved = false,
}: {
  onCancel: () => void
  onOk: () => void
  onSaveDefault: () => void
  /** Briefly true right after a save: the button reads "Saved" so the click
   *  visibly landed (there is no other feedback for a localStorage write). */
  saved?: boolean
}) {
  return (
    <div className="flex items-center gap-2 border-t border-border-strong px-3 py-2.5">
      <button
        onClick={onSaveDefault}
        className={SECONDARY_BUTTON_CLS}
        title="Use for new drawings"
        aria-live="polite"
      >
        {saved ? 'Saved ✓' : 'Save as default'}
      </button>
      <div className="flex-1" />
      <button onClick={onCancel} className={SECONDARY_BUTTON_CLS}>
        Cancel
      </button>
      <button onClick={onOk} className={primaryButtonCls(false)}>
        OK
      </button>
    </div>
  )
}
